import { dateFns as moment } from '@pcln/date-fns-utils'
import { logPackageMerchandisingPricingClick } from '../../../../common/log-ga-event/logGaEvent-cross-sell'

const cabinClassMap = {
  BEC: 'Basic Economy',
  ECO: 'Economy',
  BUS: 'Business',
  FST: 'First',
  PEC: 'Premium Economy',
}

const eventHandlerWrapper = (cbs, e) => {
  if (e.key === 'Enter' || e.keyCode === 13 || e.key === ' ' || e.keyCode === 32 || e.type === 'click') {
    // Prevents spacebar scroll to bottom action
    e.preventDefault()
    cbs.forEach((cb) => cb())
  }
}

const isMixedCabin = (segments) =>
  segments.length && segments.some((segment) => segment.cabinClass != segments[0].cabinClass)

export const returnToDepartureTab = (cb, index) => (e) => {
  const callbacks = [() => cb({ id: index + 1 })]

  eventHandlerWrapper(callbacks, e)
}

function getOvernight(slice) {
  const departingTime = slice.departInfo?.time?.dateTime
  const arrivalTime = slice.arrivalInfo?.time?.dateTime
  const departDateTime = moment(departingTime)
  const arrivalDateTime = moment(arrivalTime)
  const daysDiff = moment(arrivalDateTime.format('YYYY-MM-DD')).diff(
    moment(departDateTime.format('YYYY-MM-DD')),
    'days'
  )

  return daysDiff ? daysDiff : null
}

export const getNextSliceClick = (itinerary, cb, isPackageListings) => (e) => {
  if (isPackageListings) {
    logPackageMerchandisingPricingClick({ linkText: 'select departure' })
  }
  eventHandlerWrapper([() => cb(itinerary)], e)
}

export const goToDetailsClick = (cb, id, itinerary, gaIndex, upsellToken) => (e) => {
  const callbacks = [() => cb({ id, gaIndex, itinerary, upsellToken })]
  eventHandlerWrapper(callbacks, e)
}

export const goToCheckout =
  ({ cb, itinerary, isPackageListings }) =>
  (e) => {
    if (isPackageListings) {
      logPackageMerchandisingPricingClick({ linkText: 'select return' })
    }
    const callbacks = [() => cb({ itinerary })]
    eventHandlerWrapper(callbacks, e)
  }

export const getMultipleAirlineText = ({ marketingAirlines, airlines }) => {
  let multipleAirlineText = null
  if (Array.isArray(marketingAirlines) && marketingAirlines.length > 1) {
    const multipleAirlineList = marketingAirlines?.map(
      (operator) => airlines.find((airline) => airline.code === operator.code)?.name
    )
    multipleAirlineList.forEach((airline, index) => {
      if (index === 0) {
        multipleAirlineText = airline
      } else if (index === multipleAirlineList.length - 1) {
        multipleAirlineText = `${multipleAirlineText} & ${airline}`
      } else {
        multipleAirlineText = `${multipleAirlineText}, ${airline}`
      }
    })
  }
  return multipleAirlineText
}

export const getFareBrandBoxTexts = ({ isSinglePassenger, tripType, packageType }) => {
  let fareBrandBoxTexts = []

  if (tripType === 'RT') {
    fareBrandBoxTexts.push('round-trip')
  }

  if (!isSinglePassenger) {
    if (fareBrandBoxTexts[0]) {
      fareBrandBoxTexts[0] = `${fareBrandBoxTexts[0]}, per person`
    } else {
      fareBrandBoxTexts.push('per person')
    }
  }

  if (packageType) {
    fareBrandBoxTexts.push(`${packageType.toLowerCase()}, taxes & fees`)
  }

  return fareBrandBoxTexts
}

const getTotalPassengersCount = (passengerCount) =>
  Object.values(passengerCount)?.reduce((acc, curr) => acc + curr, 0)

export const getTotalCostText = ({ price, passengerCount, packageType }) => {
  const totalPrice = price.find((typeOfPrice) => typeOfPrice.type === 'TOTAL_PRICE')
  if (!packageType || !totalPrice.packagesPriceData || !passengerCount?.ADULT) return null

  const totalPaxCount = getTotalPassengersCount(passengerCount)
  const totalCost = Math.ceil(totalPrice.packagesPriceData.totalCostWithHotelMandatoryFees)

  return `$${totalCost} total${totalPaxCount > 1 ? ' for ' : ''}${totalPaxCount > 1 ? totalPaxCount : ''}`
}

export const getSBSItineraryData = ({
  itinerary,
  isDepartureSummary,
  isUserLoggedIn,
  activeTabId,
  departureSummaryData,
  tripType,
  isSinglePassenger,
  packageType,
  isShelves,
  passengerCount,
  hasPriorPackageSelection = false,
}) => {
  const {
    marketingAirlines,
    allFareBrandNames,
    alternateDatesTexts,
    slices,
    isCugEligible,
    upsellFareBrands,
    priceKey,
    isExchangeFlight,
    airlines,
  } = itinerary
  const { isOvernight, departInfo, arrivalInfo, layoverInfo, displayText, segments } = slices[0]

  let duration = slices[0]?.duration
  const airline = marketingAirlines[0]
  const fullAirlineName = airline.name
  const airlineTestId = `airline-name-${fullAirlineName}`

  // TODO: Check the logic again
  const overnightDays = getOvernight(slices[0])

  const arrival = {
    airportName: arrivalInfo?.airport?.name,
    airport: arrivalInfo?.airport?.code,
    time: `${arrivalInfo?.time?.displayTime}`,
    overnightDays,
  }
  const departing = {
    airportName: departInfo?.airport?.name,
    airport: departInfo?.airport?.code,
    time: `${departInfo?.time?.displayTime}`,
    dateRange: moment(departInfo?.time?.dateTime).format('ddd, MMM D'),
  }

  const transformedSegments = segments.map((segment) => {
    const { departInfo } = segment
    return {
      ...segment,
      time: departInfo?.time?.displayTime,
      airport: departInfo?.airport?.code,
      dateTime: departInfo?.time?.dateTime,
      formattedDate: departInfo?.time?.displayTime,
    }
  })

  let fareBrandName = allFareBrandNames[0]
  const roundUpPrice = (price) => {
    const amountBeforeDecimal = price?.dollar
    const amountAfterDecimal = price?.cents ? amountBeforeDecimal + '.' + price?.cents : amountBeforeDecimal
    return Math.ceil(+amountAfterDecimal)
  }
  // TODO: MOVE to fly-ql
  if (isMixedCabin(segments)) {
    fareBrandName = 'MIXED CABIN'
  } else if (!fareBrandName) {
    const cabinClass = segments?.[0]?.cabinClass
    const cabinClassName = cabinClassMap[cabinClass]
    if (cabinClassName) {
      fareBrandName = cabinClassName
    }
  }

  const matchingFareBrand = isDepartureSummary
    ? getMatchingFareBrandByPriceKey({ fareBrands: itinerary?.fareBrands, priceKey })
    : itinerary?.fareBrands?.[0]

  let price = getPriceByType({ prices: matchingFareBrand?.price, type: 'AVG_PRICE' })
  if (isShelves) {
    fareBrandName = itinerary?.fareBrandName
  }

  if (!fareBrandName && matchingFareBrand) {
    fareBrandName = matchingFareBrand?.name
  }

  // Incremental when a prior product is selected (any package type), or on RT return slice.
  const showIncrementalPricing = activeTabId > 1 || hasPriorPackageSelection

  if (activeTabId === 1 && isExchangeFlight && price.amount >= 0) {
    price.text = '+'
  } else if (showIncrementalPricing && !isDepartureSummary) {
    // fly-ql sends Package Listings incremental pricing for all slices in fareBrands[0]
    // When the user navigates back to slice 1, activeTabId may temporarily remain as 2 for a few milliseconds.
    // Need to see do some refactoring on how listings are rendered.
    // This check prevents setting the price to null during that short period for packages for car.
    if (matchingFareBrand?.pricing) {
      price = matchingFareBrand.pricing
    }

    if (!hasPriorPackageSelection) {
      // This totalPriceForMultiAdultPassenger is used in upsell tray UI
      // when there are no fare brands or an airPrice error occurs
      price['totalPriceForMultiAdultPassenger'] =
        roundUpPrice(departureSummaryData?.price) + roundUpPrice(price)
    }
  }

  const totalCostText = getTotalCostText({ price: matchingFareBrand?.price, passengerCount, packageType })
  const seatsAvailableText = itinerary?.fareBrands?.[0]?.seatsAvailableText

  const fareBrandBoxTexts = getFareBrandBoxTexts({ isSinglePassenger, tripType, packageType })

  const showMemberSignIn = !isUserLoggedIn && isCugEligible

  const multipleAirlineText = getMultipleAirlineText({
    marketingAirlines,
    airlines,
  })

  let ancillaries = matchingFareBrand?.ancillaries

  let stopsText = null
  let durationText = null
  if (isDepartureSummary) {
    stopsText = getStopsText(layoverInfo)
    durationText = duration
    duration = null
  }

  const packageSavingsAmount =
    getPriceByType({ prices: matchingFareBrand?.price, type: 'TOTAL_PRICE' })?.packagesPriceData
      ?.perPersonSavings ?? 0

  return {
    altDateText: alternateDatesTexts?.dateRange,
    airline,
    activeTabId,
    tripType,
    airlineTestId,
    ancillaries,
    layoverInfo,
    segments: transformedSegments,
    arrival,
    fareBrandName,
    departing,
    isOvernight,
    price,
    operatedByText: displayText?.operatedByText,
    seatsAvailableText,
    fareBrandBoxTexts,
    duration,
    upsellFareBrands,
    stopsText,
    durationText,
    showMemberSignIn,
    key: itinerary.id,
    multipleAirlineText,
    isSinglePassenger,
    priceKey,
    isUserLoggedIn,
    packageSavingsAmount,
    totalCostText,
    marketingAirlines,
  }
}

const getStopsText = (layovers) => {
  switch (layovers && layovers.length) {
    case 0:
      return 'Nonstop'
    case 1:
      return '1 Stop'
    case 2:
      return '2 Stops'
    default:
      return 'Nonstop'
  }
}

export const getPriceByType = ({ prices = [], type }) => prices.find((p) => p?.type === type)

export const getMatchingFareBrandByPriceKey = ({ fareBrands = [], priceKey }) => {
  return fareBrands.find((fb) => fb?.priceKey === priceKey) || {}
}

export const getMatchingFareBrandIndex = ({ fareBrands = [], priceKey }) => {
  return fareBrands.findIndex((fb) => fb?.priceKey === priceKey)
}
